import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router';
import { AlertTriangle, X } from 'lucide-react';
import { Sidebar } from '@/components/Sidebar';
import { SidebarTutorial } from '@/components/SidebarTutorial';
import { apiGet } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

function TokenWarningBanner() {
  const [pct, setPct] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    apiGet<{ tokens: { used: number; limit: number } }>('/billing/usage')
      .then((u) => {
        const p = u.tokens.limit > 0 ? (u.tokens.used / u.tokens.limit) * 100 : 0;
        setPct(p);
      })
      .catch(() => {});
  }, []);

  if (dismissed || pct <= 80) return null;

  const critical = pct > 95;

  return (
    <div className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg mb-4 ${
      critical
        ? 'bg-error/10 text-error border border-error/20'
        : 'bg-warning/10 text-warning border border-warning/20'
    }`}>
      <AlertTriangle size={14} className="shrink-0" />
      <span className="flex-1">
        {critical
          ? 'You\'ve used almost all your tokens this month. Upgrade to keep generating.'
          : 'You\'ve used over 80% of your monthly tokens.'}
      </span>
      <Link to="/settings/billing" className="underline underline-offset-2 shrink-0 hover:opacity-80">
        {critical ? 'Upgrade now' : 'View usage'}
      </Link>
      <button onClick={() => setDismissed(true)} className="p-0.5 rounded hover:opacity-60 shrink-0">
        <X size={12} />
      </button>
    </div>
  );
}

export function AppLayout() {
  const toast = useToast();

  useEffect(() => {
    const handler = () => {
      toast.warning('Session expired. Redirecting to login...');
    };
    window.addEventListener('session-expired', handler);
    return () => window.removeEventListener('session-expired', handler);
  }, [toast]);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <SidebarTutorial />
      <main className="flex-1 min-w-0 p-6 lg:p-8">
        <TokenWarningBanner />
        <Outlet />
      </main>
    </div>
  );
}
